import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import {
  User, Instagram, RefreshCw, Save, Briefcase, Globe, Target,
  Heart, UserPlus, Eye, MessageCircle, Clock, Zap,
  Smartphone, Activity, BookOpen, Send, FileText, Plus, Trash2,
  Search, Filter, UserMinus, X, CheckSquare, Square, Users
} from 'lucide-react';
import axios from 'axios';
import { getProfilePicUrl, getLocalProfilePicUrl } from '../config/imageUrls';

/* ═══════════════════════════════════════════════
   STYLED COMPONENTS
   ═══════════════════════════════════════════════ */

const Page = styled.div`width: 100%;`;

const TopBanner = styled.div`
  display: flex; align-items: center; gap: 16px; padding: 18px 24px;
  border-radius: 14px; margin-bottom: 20px;
  background: linear-gradient(135deg, var(--surface), var(--card-bg));
  border: 1px solid var(--card-border);
`;

const Avatar = styled.div`
  width: 56px; height: 56px; border-radius: 50%; background: var(--gradient);
  display: flex; align-items: center; justify-content: center; color: #fff;
  font-size: 20px; font-weight: 700; flex-shrink: 0; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const BannerMeta = styled.div`
  flex: 1;
  h2 { font-size: 18px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
`;

const MetaRow = styled.div`
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
`;

const MetaTag = styled.span`
  display: inline-flex; align-items: center; gap: 4px;
  font-size: 11px; color: var(--text-muted); font-weight: 500;
`;

const TypeBadge = styled.span`
  padding: 3px 10px; border-radius: 12px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .5px;
  background: ${p => p.$type === 'CLIENT' ? 'rgba(245,158,11,.15)' : p.$type === 'OUR' ? 'rgba(34,197,94,.15)' : 'var(--surface)'};
  color: ${p => p.$type === 'CLIENT' ? '#f59e0b' : p.$type === 'OUR' ? '#22c55e' : 'var(--primary)'};
`;

const ProfileStat = styled.div`
  text-align: center; padding: 0 14px;
  div:first-child { font-size: 17px; font-weight: 700; color: var(--text-primary); }
  div:last-child { font-size: 9px; color: var(--text-muted); text-transform: uppercase; letter-spacing: .3px; }
`;

const SaveBtn = styled.button`
  display: flex; align-items: center; gap: 6px; padding: 10px 20px;
  border-radius: 10px; border: none; font-weight: 700; font-size: 12px;
  background: var(--gradient); color: #fff; cursor: pointer; transition: all .15s;
  &:hover { transform: translateY(-1px); box-shadow: var(--shadow-hover); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

/* ── Stats Row ── */
const StatsRow = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 10px; margin-bottom: 20px;
`;

const StatCard = styled.div`
  padding: 14px; border-radius: 10px;
  background: var(--card-bg); border: 1px solid var(--card-border);
  text-align: center;
`;

const StatIcon = styled.div`
  display: flex; justify-content: center; margin-bottom: 4px;
  color: ${p => p.$color || 'var(--primary)'};
`;

const StatVal = styled.div`
  font-size: 20px; font-weight: 800; color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
`;

const StatLabel = styled.div`
  font-size: 9px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .3px; margin-top: 2px;
`;

/* ── Form sections ── */
const TwoColumnLayout = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 16px; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;

const Column = styled.div`
  display: flex; flex-direction: column; gap: 16px;
`;

const Section = styled.div`
  padding: 18px 20px; border-radius: 12px;
  background: var(--card-bg); border: 1px solid var(--card-border);
`;

const SectionTitle = styled.h3`
  font-size: 14px; font-weight: 700; color: var(--text-dark);
  display: flex; align-items: center; gap: 8px; margin: 0 0 14px;
  svg { color: var(--primary); }
`;

const FieldGroup = styled.div`
  margin-bottom: 12px;
  &:last-child { margin-bottom: 0; }
`;

const ParamGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const ParamField = styled.div``;

const ParamFieldWide = styled.div`
  grid-column: 1 / -1;
`;

const FieldLabel = styled.label`
  display: block; font-size: 10px; font-weight: 600; color: var(--text-muted);
  text-transform: uppercase; letter-spacing: .3px; margin-bottom: 4px;
`;

const FieldInput = styled.input`
  width: 100%; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px; transition: border-color .15s;
  &:focus { border-color: var(--primary); outline: none; }
  &::placeholder { color: var(--text-muted); }
`;

const FieldTextarea = styled.textarea`
  width: 100%; padding: 8px 12px; border-radius: 8px; min-height: 70px; resize: vertical;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px; font-family: inherit; transition: border-color .15s;
  &:focus { border-color: var(--primary); outline: none; }
  &::placeholder { color: var(--text-muted); }
`;

const FieldSelect = styled.select`
  width: 100%; padding: 8px 12px; border-radius: 8px;
  border: 1px solid var(--border); background: var(--surface);
  color: var(--text-primary); font-size: 12px;
  &:focus { border-color: var(--primary); outline: none; }
`;

const EmptyState = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 50vh; color: var(--text-muted); text-align: center;
  svg { opacity: .2; margin-bottom: 12px; }
  p { font-size: 13px; }
`;

/* ── Account Picker (no linked account) ── */
const PickerWrap = styled.div`
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 50vh; gap: 20px;
`;

const PickerTitle = styled.h2`
  font-size: 18px; font-weight: 700; color: var(--text-primary);
  display: flex; align-items: center; gap: 10px;
  svg { color: var(--primary); }
`;

const PickerSubtitle = styled.p`
  font-size: 13px; color: var(--text-muted); margin: -10px 0 0;
`;

const PickerGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px; width: 100%; max-width: 700px;
`;

const PickerCard = styled.button`
  display: flex; align-items: center; gap: 12px; padding: 14px 16px;
  border-radius: 12px; border: 2px solid var(--card-border);
  background: var(--card-bg); cursor: pointer; transition: all .2s;
  text-align: left;
  &:hover { border-color: var(--primary); transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(165,145,239,.15); }
  &:disabled { opacity: .5; cursor: not-allowed; transform: none; }
`;

const PickerAvatar = styled.div`
  width: 44px; height: 44px; border-radius: 50%; background: var(--gradient);
  display: flex; align-items: center; justify-content: center; color: #fff;
  font-size: 16px; font-weight: 700; flex-shrink: 0; overflow: hidden;
  img { width: 100%; height: 100%; object-fit: cover; }
`;

const PickerInfo = styled.div`
  flex: 1; min-width: 0;
  .pk-name { font-size: 13px; font-weight: 700; color: var(--text-primary);
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .pk-type { font-size: 10px; color: var(--text-muted); margin-top: 2px; }
`;

/* ═══════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════ */

const AccountProfile = ({ selectedDevice }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [todayStats, setTodayStats] = useState({});
  const [form, setForm] = useState({});
  const [lists, setLists] = useState([]);
  const [editingList, setEditingList] = useState(null);
  const [listContent, setListContent] = useState('');
  const [listSaving, setListSaving] = useState(false);
  const [allAccounts, setAllAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [originalAccountId, setOriginalAccountId] = useState(null);
  const [linking, setLinking] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newAccountType, setNewAccountType] = useState('OUR');
  const [newNiche, setNewNiche] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [creating, setCreating] = useState(false);
  // Profile Picker modal state
  const [showProfilePicker, setShowProfilePicker] = useState(false);
  const [pickerListFile, setPickerListFile] = useState(null); // { filename, type }
  const [interactedProfiles, setInteractedProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);
  const [pickerFilter, setPickerFilter] = useState('all');
  const [pickerSearch, setPickerSearch] = useState('');
  const [selectedProfiles, setSelectedProfiles] = useState(new Set());
  const [addingProfiles, setAddingProfiles] = useState(false);
  const [deviceAccounts, setDeviceAccounts] = useState([]);
  const [, setInstalledClones] = useState([]);
  // Target picker modal state
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  const [targetSearch, setTargetSearch] = useState('');
  const [targetResults, setTargetResults] = useState([]);
  const [targetSearching, setTargetSearching] = useState(false);
  const [selectedTargets, setSelectedTargets] = useState(new Set());
  const [addingTargets, setAddingTargets] = useState(false);
  const [interactList, setInteractList] = useState([]);
  const [interactListLoading, setInteractListLoading] = useState(false);

  const accountId = selectedAccountId || selectedDevice?.account_id;
  const accountChanged = selectedAccountId && originalAccountId && selectedAccountId !== originalAccountId;

  // Fetch all instagram accounts for the selector
  useEffect(() => {
    axios.get('/api/instagram-accounts').then(r => {
      if (r.data.success) setAllAccounts(r.data.accounts || []);
    }).catch(() => {});
  }, []);

  // Fetch installed Instagram clones on device (via ADB)
  useEffect(() => {
    if (selectedDevice?.device_id) {
      axios.get(`/api/devices/apk/instagram-clones/${selectedDevice.device_id}`)
        .then(r => setInstalledClones(r.data?.clones || []))
        .catch(() => setInstalledClones([]));
    }
  }, [selectedDevice?.device_id]);

  // Fetch all accounts linked to this device (for package selector)
  const refreshDeviceAccounts = useCallback(() => {
    if (selectedDevice?.device_id) {
      axios.get(`/api/instagram-packages/device-accounts/${selectedDevice.device_id}`).then(r => {
        if (r.data.success) setDeviceAccounts(r.data.accounts || []);
      }).catch(() => setDeviceAccounts([]));
    }
  }, [selectedDevice?.device_id]);

  useEffect(() => { refreshDeviceAccounts(); }, [refreshDeviceAccounts]);

  // Auto-select device's account when device changes
  useEffect(() => {
    setSelectedAccountId(selectedDevice?.account_id || null);
    setOriginalAccountId(selectedDevice?.account_id || null);
  }, [selectedDevice?.device_id, selectedDevice?.account_id]);

  const fetchProfile = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [profR, liveR] = await Promise.all([
        axios.get(`/api/account-profile/${accountId}`),
        axios.get(`/api/live-actions/${accountId}?limit=1`).catch(() => ({ data: {} }))
      ]);
      const p = profR.data.profile;
      setProfile(p);
      setTodayStats(liveR.data.stats || {});
      setForm({
        account_type: p.account_type || '',
        business_name: p.business_name || '',
        business_niche: p.business_niche || '',
        business_product: p.business_product || '',
        business_website: p.business_website || '',
        business_language: p.business_language || 'Français',
        strategy_objective: p.strategy_objective || '',
        strategy_audience: p.strategy_audience || '',
        personality_tone: p.personality_tone || '',
        personality_context: p.personality_context || '',
        gemini_prompt: p.gemini_prompt || '',
        total_scrape_limit: p.total_scrape_limit || '',
        scrape_limit_per_source: p.scrape_limit_per_source || '',
        card_workflow: p.card_workflow || '',
        // Interaction params
        successful_interactions_limit_per_source: p.successful_interactions_limit_per_source || '30-40',
        likes_count: p.likes_count || '1-2',
        story_like_percentage: p.story_like_percentage ?? 10,
        follow_percentage: p.follow_percentage ?? 0,
        stories_count: p.stories_count || '2-4',
        total_story_limit: p.total_story_limit ?? 120,
        total_likes_limit: p.total_likes_limit ?? 250,
        session_length_in_mins_limit: p.session_length_in_mins_limit ?? 240,
        interaction_recheck_hours: p.interaction_recheck_hours ?? 2016,
        like_target_for_the_day: p.like_target_for_the_day ?? 150,
        follow_target_for_the_day: p.follow_target_for_the_day ?? 100,
        // Unfollow params
        unfollow_limit: p.unfollow_limit ?? '',
        min_following_hours: p.min_following_hours ?? '',
        unfollow_delay: p.unfollow_delay || '',
        unfollow_type_script: p.unfollow_type_script || 'database-global-search',
        unfollow_count: p.unfollow_count || '',
        unfollow_older_than_days: p.unfollow_older_than_days ?? 7,
        // DM & Comment params
        comment_percentage: p.comment_percentage ?? 0,
        dm_list: p.dm_list || '',
        dm_new_followers: p.dm_new_followers || 'No',
        dm_to_bot_followed: p.dm_to_bot_followed || 'No',
        dm_daily_limit: p.dm_daily_limit ?? 0,
      });
    } catch (e) { setProfile(null); }
    setLoading(false);
  }, [accountId]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const updateField = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  const saveProfile = async () => {
    if (!accountId) return;
    setSaving(true);
    try {
      // If account was changed, update the device↔account association first
      if (accountChanged && selectedDevice?.device_id) {
        await axios.put('/api/instagram-accounts/device-association', {
          device_id: selectedDevice.device_id,
          new_account_id: selectedAccountId,
          old_account_id: originalAccountId
        });
        setOriginalAccountId(selectedAccountId);
      }
      // Save profile fields + interaction params (include device_id to scope card update)
      await axios.put(`/api/account-profile/${accountId}`, { ...form, device_id: selectedDevice?.device_id });
    } catch (e) { console.error('Save error:', e); }
    setSaving(false);
  };

  // ── Account Lists ──
  const username = profile?.username;

  const fetchLists = useCallback(async () => {
    if (!username) return;
    try {
      const r = await axios.get(`/api/account-lists/${username}`);
      setLists(r.data.lists || []);
    } catch { setLists([]); }
  }, [username]);

  useEffect(() => { fetchLists(); }, [fetchLists]);

  const openListEditor = (list) => {
    setEditingList(list.filename);
    setListContent(list.entries.join('\n'));
  };

  const saveList = async () => {
    if (!username || !editingList) return;
    setListSaving(true);
    try {
      await axios.put(`/api/account-lists/${username}/save`, { filename: editingList, content: listContent });
      await fetchLists();
      setEditingList(null);
    } catch { /* silent */ }
    setListSaving(false);
  };

  const createList = async (listType) => {
    if (!username) return;
    try {
      await axios.post(`/api/account-lists/${username}/create`, { listType });
      await fetchLists();
    } catch { /* silent */ }
  };

  const deleteList = async (filename) => {
    if (!username || !window.confirm(`Supprimer ${filename} ?`)) return;
    try {
      await axios.delete(`/api/account-lists/${username}/delete`, { data: { filename } });
      if (editingList === filename) setEditingList(null);
      await fetchLists();
    } catch { /* silent */ }
  };

  // Profile Picker: fetch interacted profiles from DB
  const fetchInteractedProfiles = useCallback(async (filter = 'all') => {
    if (!accountId) return;
    setProfilesLoading(true);
    try {
      const r = await axios.get(`/api/account-lists/${accountId}/interacted-profiles`, {
        params: { actionType: filter }
      });
      if (r.data.success) setInteractedProfiles(r.data.profiles || []);
    } catch (e) {
      console.error('Error fetching interacted profiles:', e);
      setInteractedProfiles([]);
    }
    setProfilesLoading(false);
  }, [accountId]);

  const openProfilePicker = (list) => {
    setPickerListFile({ filename: list.filename, type: list.type });
    setSelectedProfiles(new Set());
    setPickerSearch('');
    setPickerFilter('all');
    setShowProfilePicker(true);
    fetchInteractedProfiles('all');
  };

  const addSelectedToList = async () => {
    if (!username || !pickerListFile || selectedProfiles.size === 0) return;
    setAddingProfiles(true);
    try {
      await axios.post(`/api/account-lists/${username}/add`, {
        listType: pickerListFile.type,
        filename: pickerListFile.filename,
        usernames: Array.from(selectedProfiles)
      });
      await fetchLists();
      setShowProfilePicker(false);
      setSelectedProfiles(new Set());
    } catch (e) {
      console.error('Error adding profiles to list:', e);
    }
    setAddingProfiles(false);
  };

  // ── Target Picker: search instagram_profile_visits and add to instagram_interact_lists ──
  const searchTargetProfiles = useCallback(async (q) => {
    if (!q || q.length < 2) { setTargetResults([]); return; }
    setTargetSearching(true);
    try {
      const r = await axios.get('/api/profiles-search', { params: { search: q, limit: 50 } });
      setTargetResults(r.data.profiles || []);
    } catch { setTargetResults([]); }
    setTargetSearching(false);
  }, []);

  const fetchInteractList = useCallback(async () => {
    if (!accountId) return;
    setInteractListLoading(true);
    try {
      const r = await axios.get(`/api/account-lists/interact-db/${accountId}`);
      setInteractList(r.data.entries || []);
    } catch { setInteractList([]); }
    setInteractListLoading(false);
  }, [accountId]);

  const addTargetsToInteractList = async () => {
    if (!accountId || selectedTargets.size === 0) return;
    setAddingTargets(true);
    try {
      const profiles = Array.from(selectedTargets).map(u => ({ username: u, source: 'manual', source_type: 'user' }));
      const r = await axios.post(`/api/account-lists/interact-db/${accountId}/add`, { profiles });
      alert(`✅ ${r.data.added} profil(s) ajouté(s)${r.data.skipped > 0 ? ` (${r.data.skipped} déjà présent(s))` : ''}`);
      setSelectedTargets(new Set());
      fetchInteractList();
    } catch (e) {
      alert(`❌ Erreur: ${e.response?.data?.error || e.message}`);
    }
    setAddingTargets(false);
  };

  const removeFromInteractList = async (usernames) => {
    if (!accountId) return;
    try {
      await axios.delete(`/api/account-lists/interact-db/${accountId}/remove`, { data: { usernames } });
      fetchInteractList();
    } catch { /* silent */ }
  };

  const toggleTarget = (uname) => {
    setSelectedTargets(prev => {
      const next = new Set(prev);
      next.has(uname) ? next.delete(uname) : next.add(uname);
      return next;
    });
  };

  const toggleProfile = (uname) => {
    setSelectedProfiles(prev => {
      const next = new Set(prev);
      if (next.has(uname)) next.delete(uname);
      else next.add(uname);
      return next;
    });
  };

  const toggleAllVisible = (filteredList) => {
    const allSelected = filteredList.every(p => selectedProfiles.has(p.username));
    if (allSelected) {
      setSelectedProfiles(prev => {
        const next = new Set(prev);
        filteredList.forEach(p => next.delete(p.username));
        return next;
      });
    } else {
      setSelectedProfiles(prev => {
        const next = new Set(prev);
        filteredList.forEach(p => next.add(p.username));
        return next;
      });
    }
  };

  // Link an account to this device
  const linkAccount = async (acctId) => {
    if (!selectedDevice?.device_id || !acctId) return;
    setLinking(true);
    try {
      await axios.put('/api/instagram-accounts/device-association', {
        device_id: selectedDevice.device_id,
        new_account_id: acctId
      });
      setSelectedAccountId(acctId);
      setOriginalAccountId(acctId);
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setLinking(false);
  };

  if (!selectedDevice) {
    return (
      <Page><EmptyState>
        <User size={48} />
        <p>Sélectionnez un appareil</p>
      </EmptyState></Page>
    );
  }

  const createAndLinkAccount = async () => {
    if (!newUsername.trim() || !selectedDevice?.device_id) return;
    setCreating(true);
    try {
      const r = await axios.post('/api/instagram-accounts/create', {
        username: newUsername.trim().replace(/^@/, ''),
        password: newPassword,
        account_type: newAccountType,
        business_niche: newNiche,
        email: newEmail
      });
      if (r.data.success) {
        const newId = r.data.account.id;
        setAllAccounts(prev => [...prev, { id: newId, username: r.data.account.username, account_type: newAccountType }]);
        await linkAccount(newId);
        setShowCreateForm(false);
        setNewUsername(''); setNewPassword(''); setNewNiche(''); setNewEmail('');
      }
    } catch (e) {
      alert('Erreur: ' + (e.response?.data?.error || e.message));
    }
    setCreating(false);
  };

  if (!accountId) {
    return (
      <Page>
        <PickerWrap>
          <PickerTitle><Instagram size={22} /> Associer un compte Instagram</PickerTitle>
          <PickerSubtitle>Aucun compte n'est lié à {selectedDevice.device_name || selectedDevice.device_id}. Choisissez-en un ou créez-en un nouveau :</PickerSubtitle>

          {/* ── Create New Account Form ── */}
          {showCreateForm ? (
            <div style={{ width: '100%', maxWidth: 480, background: 'var(--card-bg)', border: '2px solid var(--primary)', borderRadius: 14, padding: 20 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Plus size={16} /> Nouveau compte Instagram
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>Nom d'utilisateur *</label>
                    <input
                      value={newUsername}
                      onChange={e => setNewUsername(e.target.value)}
                      placeholder="ex: mon_compte_insta"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13 }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>Mot de passe</label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={e => setNewPassword(e.target.value)}
                      placeholder="mot de passe"
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13 }}
                    />
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>Type de compte</label>
                    <select
                      value={newAccountType}
                      onChange={e => setNewAccountType(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 12 }}
                    >
                      <option value="OUR">OUR (Notre compte)</option>
                      <option value="CLIENT">CLIENT</option>
                      <option value="IMPULSE">IMPULSE</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>Niche / Thématique</label>
                    <input
                      value={newNiche}
                      onChange={e => setNewNiche(e.target.value)}
                      placeholder="ex: fitness, food, tech..."
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13 }}
                    />
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.3px' }}>Email associé</label>
                  <input
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    placeholder="email@gmail.com"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 13 }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button onClick={() => setShowCreateForm(false)} style={{ flex: 1, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Annuler
                  </button>
                  <button onClick={createAndLinkAccount} disabled={!newUsername.trim() || creating} style={{ flex: 1, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--gradient)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (!newUsername.trim() || creating) ? .5 : 1 }}>
                    {creating ? 'Création...' : 'Créer & Associer'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <PickerCard onClick={() => setShowCreateForm(true)} style={{ maxWidth: 420, border: '2px dashed var(--primary)', justifyContent: 'center' }}>
              <Plus size={20} style={{ color: 'var(--primary)' }} />
              <PickerInfo>
                <div className="pk-name" style={{ color: 'var(--primary)' }}>Créer un nouveau compte</div>
                <div className="pk-type">Ajouter un compte Instagram et l'associer à ce device</div>
              </PickerInfo>
            </PickerCard>
          )}

          {/* ── Existing Accounts ── */}
          {allAccounts.length === 0 ? (
            <EmptyState><User size={32} /><p>Aucun compte Instagram disponible</p></EmptyState>
          ) : (
            <PickerGrid>
              {allAccounts.map(a => (
                <PickerCard key={a.id} onClick={() => linkAccount(a.id)} disabled={linking}>
                  <PickerAvatar>
                    {a.username ? (
                      <img
                        src={getLocalProfilePicUrl(a.username)}
                        alt=""
                        onError={e => {
                          const step = parseInt(e.target.dataset.fb || '0');
                          if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(a.username); }
                          else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${a.username}`; }
                          else { e.target.style.display = 'none'; e.target.parentElement.textContent = (a.username || '?')[0].toUpperCase(); }
                        }}
                      />
                    ) : '?'}
                  </PickerAvatar>
                  <PickerInfo>
                    <div className="pk-name">@{a.username || a.instagramUsername || `ID ${a.id}`}</div>
                    <div className="pk-type">{a.account_type || 'Non défini'}</div>
                  </PickerInfo>
                </PickerCard>
              ))}
            </PickerGrid>
          )}
        </PickerWrap>
      </Page>
    );
  }

  if (loading && !profile) {
    return <Page><EmptyState><RefreshCw size={32} /><p>Chargement...</p></EmptyState></Page>;
  }

  const p = profile || {};
  const likes = parseInt(todayStats.likes) || 0;
  const follows = parseInt(todayStats.follows) || 0;
  const unfollows = parseInt(todayStats.unfollows) || 0;
  const profileViews = parseInt(todayStats.profile_views) || 0;
  const storyViews = parseInt(todayStats.story_views) || 0;
  const comments = parseInt(todayStats.comments) || 0;
  const dms = parseInt(todayStats.dms) || 0;
  const uniqueUsers = parseInt(todayStats.unique_users) || 0;
  const total = parseInt(todayStats.total) || 0;

  return (
    <Page>
      {/* ── Top Banner ── */}
      <TopBanner>
        <Avatar>
          {p.username ? <img src={getLocalProfilePicUrl(p.username)} alt=""
            onError={e => {
              const step = parseInt(e.target.dataset.fb || '0');
              if (step === 0) { e.target.dataset.fb = '1'; e.target.src = getProfilePicUrl(p.username); }
              else if (step === 1) { e.target.dataset.fb = '2'; e.target.src = `https://unavatar.io/instagram/${p.username}`; }
              else { e.target.style.display = 'none'; }
            }} /> :
            (p.username || '?')[0].toUpperCase()}
        </Avatar>
        <BannerMeta>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            @{p.username || '—'}
            {(() => {
              const mySlot = deviceAccounts.find(da => String(da.account_id) === String(accountId));
              if (!mySlot) return null;
              const appId = mySlot.app_id || 'com.instagram.android';
              const isOrig = appId === 'com.instagram.android';
              const label = mySlot.package_label || (isOrig ? 'Instagram' : `IG ${(mySlot.suffix_letter || appId.slice(-1)).toUpperCase()}`);
              const color = mySlot.package_color || (isOrig ? '#E1306C' : '#6366f1');
              return (
                <span style={{
                  fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 5,
                  background: `${color}20`, color, border: `1px solid ${color}40`,
                  display: 'inline-flex', alignItems: 'center', gap: 4, lineHeight: 1.3
                }}>
                  <Instagram size={11} />
                  {label}
                </span>
              );
            })()}
          </h2>
          <MetaRow>
            <MetaTag><Smartphone size={11} /> {p.device_name || p.device_id || '—'}</MetaTag>
            <MetaTag><Clock size={11} /> ID: {p.id}</MetaTag>
            {p.account_type && <TypeBadge $type={p.account_type?.toUpperCase()}>{p.account_type}</TypeBadge>}
          </MetaRow>
        </BannerMeta>
        <ProfileStat><div>{p.posts_count || '—'}</div><div>Posts</div></ProfileStat>
        <ProfileStat><div>{p.followers_count || '—'}</div><div>Followers</div></ProfileStat>
        <ProfileStat><div>{p.following_count || '—'}</div><div>Following</div></ProfileStat>
        <SaveBtn onClick={saveProfile} disabled={saving}>
          <Save size={14} /> {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </SaveBtn>
      </TopBanner>

      {/* Package slots moved to sidebar — badge shown in top banner */}

      {/* ── Today's Stats ── */}
      <StatsRow>
        <StatCard><StatIcon $color="#ef4444"><Heart size={15} /></StatIcon><StatVal>{likes}</StatVal><StatLabel>Likes</StatLabel></StatCard>
        <StatCard><StatIcon $color="#22c55e"><UserPlus size={15} /></StatIcon><StatVal>{follows}</StatVal><StatLabel>Follows</StatLabel></StatCard>
        <StatCard><StatIcon $color="#f59e0b"><UserMinus size={15} /></StatIcon><StatVal>{unfollows}</StatVal><StatLabel>Unfollows</StatLabel></StatCard>
        <StatCard><StatIcon $color="#a591ef"><Eye size={15} /></StatIcon><StatVal>{profileViews}</StatVal><StatLabel>Profils</StatLabel></StatCard>
        <StatCard><StatIcon $color="#f59e0b"><Eye size={15} /></StatIcon><StatVal>{storyViews}</StatVal><StatLabel>Stories</StatLabel></StatCard>
        <StatCard><StatIcon $color="#3b82f6"><MessageCircle size={15} /></StatIcon><StatVal>{comments}</StatVal><StatLabel>Comments</StatLabel></StatCard>
        <StatCard><StatIcon $color="#8b5cf6"><Send size={15} /></StatIcon><StatVal>{dms}</StatVal><StatLabel>DMs</StatLabel></StatCard>
        <StatCard><StatIcon $color="#ec4899"><Users size={15} /></StatIcon><StatVal>{uniqueUsers}</StatVal><StatLabel>Uniques</StatLabel></StatCard>
        <StatCard><StatIcon $color="#06b6d4"><Activity size={15} /></StatIcon><StatVal>{total}</StatVal><StatLabel>Total</StatLabel></StatCard>
      </StatsRow>

      {/* ══ Two-Column Layout: Profile & Identity (left) | Automation Params (right) ══ */}
      <TwoColumnLayout>
        {/* ── LEFT COLUMN: Identity, Business, Personality, Prompt, Notes ── */}
        <Column>
          <Section>
            <SectionTitle><User size={16} /> Identité</SectionTitle>
            <FieldGroup>
              <FieldLabel>Nom d'affichage</FieldLabel>
              <FieldInput value={form.business_name} onChange={e => updateField('business_name', e.target.value)} placeholder="Mon Business" />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Site web</FieldLabel>
              <FieldInput value={form.business_website} onChange={e => updateField('business_website', e.target.value)} placeholder="https://monsite.com" />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Langue principale</FieldLabel>
              <FieldSelect value={form.business_language} onChange={e => updateField('business_language', e.target.value)}>
                <option value="Français">Français</option>
                <option value="English">English</option>
                <option value="Español">Español</option>
                <option value="Deutsch">Deutsch</option>
                <option value="Italiano">Italiano</option>
              </FieldSelect>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Type de compte</FieldLabel>
              <FieldSelect value={form.account_type} onChange={e => updateField('account_type', e.target.value)}>
                <option value="">Non défini</option>
                <option value="OUR">OUR (Notre compte)</option>
                <option value="CLIENT">CLIENT</option>
                <option value="QLICKR">QLICKR</option>
                <option value="SECONDAIRE">SECONDAIRE</option>
                <option value="OLD">OLD</option>
              </FieldSelect>
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Workflow associé</FieldLabel>
              <FieldInput value={form.card_workflow} onChange={e => updateField('card_workflow', e.target.value)} placeholder="ex: A, B, C, D..." />
            </FieldGroup>
          </Section>

          <Section>
            <SectionTitle><Briefcase size={16} /> Business & Stratégie</SectionTitle>
            <FieldGroup>
              <FieldLabel>Niche / Secteur</FieldLabel>
              <FieldInput value={form.business_niche} onChange={e => updateField('business_niche', e.target.value)} placeholder="Growth marketing, e-commerce, fitness..." />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Produit / Service</FieldLabel>
              <FieldTextarea value={form.business_product} onChange={e => updateField('business_product', e.target.value)} placeholder="Service de boost Instagram : followers, likes, engagement..." />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Objectif principal</FieldLabel>
              <FieldTextarea value={form.strategy_objective} onChange={e => updateField('strategy_objective', e.target.value)}
                placeholder="Acquérir des clients via les commentaires, générer du trafic vers le site..." />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Audience cible</FieldLabel>
              <FieldTextarea value={form.strategy_audience} onChange={e => updateField('strategy_audience', e.target.value)}
                placeholder="Entrepreneurs, créateurs de contenu, PME qui veulent grandir sur Instagram..." />
            </FieldGroup>
          </Section>

          <Section>
            <SectionTitle><BookOpen size={16} /> Personnalité</SectionTitle>
            <FieldGroup>
              <FieldLabel>Ton / Personnalité</FieldLabel>
              <FieldTextarea value={form.personality_tone} onChange={e => updateField('personality_tone', e.target.value)}
                placeholder="Professionnel mais accessible, bienveillant, expert du domaine..." />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>Contexte additionnel</FieldLabel>
              <FieldTextarea value={form.personality_context} onChange={e => updateField('personality_context', e.target.value)}
                placeholder="Informations supplémentaires que l'IA doit connaître pour mieux répondre..." />
            </FieldGroup>
          </Section>

          <Section>
            <SectionTitle><Zap size={16} /> Prompt Gemini</SectionTitle>
            <FieldGroup>
              <FieldLabel>Prompt personnalisé pour les réponses IA</FieldLabel>
              <FieldTextarea
                value={form.gemini_prompt}
                onChange={e => updateField('gemini_prompt', e.target.value)}
                placeholder="Instructions spécifiques pour Gemini lors de la génération de commentaires..."
                style={{ minHeight: 80 }}
              />
            </FieldGroup>
          </Section>
        </Column>

        {/* ── RIGHT COLUMN: All Interaction & Automation Parameters (2-sub-col grid) ── */}
        <Column>
          <Section>
            <SectionTitle><Heart size={16} /> Paramètres d'interaction</SectionTitle>
            <ParamGrid>
              <ParamField>
                <FieldLabel>Interactions / source</FieldLabel>
                <FieldInput value={form.successful_interactions_limit_per_source} onChange={e => updateField('successful_interactions_limit_per_source', e.target.value)} placeholder="30-40" />
              </ParamField>
              <ParamField>
                <FieldLabel>Likes par profil</FieldLabel>
                <FieldInput value={form.likes_count} onChange={e => updateField('likes_count', e.target.value)} placeholder="1-2" />
              </ParamField>
              <ParamField>
                <FieldLabel>Stories par profil</FieldLabel>
                <FieldInput value={form.stories_count} onChange={e => updateField('stories_count', e.target.value)} placeholder="2-4" />
              </ParamField>
              <ParamField>
                <FieldLabel>% Story Like</FieldLabel>
                <FieldInput type="number" value={form.story_like_percentage} onChange={e => updateField('story_like_percentage', e.target.value)} placeholder="10" />
              </ParamField>
              <ParamField>
                <FieldLabel>% Follow</FieldLabel>
                <FieldInput type="number" value={form.follow_percentage} onChange={e => updateField('follow_percentage', e.target.value)} placeholder="0" />
              </ParamField>
              <ParamField>
                <FieldLabel>% Commentaire</FieldLabel>
                <FieldInput type="number" value={form.comment_percentage} onChange={e => updateField('comment_percentage', e.target.value)} placeholder="0" />
              </ParamField>
            </ParamGrid>
          </Section>

          <Section>
            <SectionTitle><Activity size={16} /> Limites de session</SectionTitle>
            <ParamGrid>
              <ParamField>
                <FieldLabel>Limite stories / jour</FieldLabel>
                <FieldInput type="number" value={form.total_story_limit} onChange={e => updateField('total_story_limit', e.target.value)} placeholder="120" />
              </ParamField>
              <ParamField>
                <FieldLabel>Limite likes / jour</FieldLabel>
                <FieldInput type="number" value={form.total_likes_limit} onChange={e => updateField('total_likes_limit', e.target.value)} placeholder="250" />
              </ParamField>
              <ParamField>
                <FieldLabel>Objectif likes / jour</FieldLabel>
                <FieldInput type="number" value={form.like_target_for_the_day} onChange={e => updateField('like_target_for_the_day', e.target.value)} placeholder="150" />
              </ParamField>
              <ParamField>
                <FieldLabel>Objectif follows / jour</FieldLabel>
                <FieldInput type="number" value={form.follow_target_for_the_day} onChange={e => updateField('follow_target_for_the_day', e.target.value)} placeholder="100" />
              </ParamField>
              <ParamFieldWide>
                <FieldLabel>Durée session max (min)</FieldLabel>
                <FieldInput type="number" value={form.session_length_in_mins_limit} onChange={e => updateField('session_length_in_mins_limit', e.target.value)} placeholder="240" />
              </ParamFieldWide>
              <ParamFieldWide>
                <FieldLabel>Recheck interactions (heures)</FieldLabel>
                <FieldInput type="number" value={form.interaction_recheck_hours} onChange={e => updateField('interaction_recheck_hours', e.target.value)} placeholder="2016 (84 jours)" />
              </ParamFieldWide>
            </ParamGrid>
          </Section>

          <Section>
            <SectionTitle><UserPlus size={16} /> Paramètres Unfollow</SectionTitle>
            <ParamGrid>
              <ParamFieldWide>
                <FieldLabel>Type de script unfollow</FieldLabel>
                <FieldSelect value={form.unfollow_type_script} onChange={e => updateField('unfollow_type_script', e.target.value)}>
                  <option value="database-global-search">Database Global Search</option>
                  <option value="from-list">From List</option>
                  <option value="oldest-first">Oldest First</option>
                </FieldSelect>
              </ParamFieldWide>
              <ParamField>
                <FieldLabel>Limite / session</FieldLabel>
                <FieldInput type="number" value={form.unfollow_limit} onChange={e => updateField('unfollow_limit', e.target.value)} placeholder="Illimité" />
              </ParamField>
              <ParamField>
                <FieldLabel>Nombre d'unfollows</FieldLabel>
                <FieldInput type="number" value={form.unfollow_count} onChange={e => updateField('unfollow_count', e.target.value)} placeholder="Auto" />
              </ParamField>
              <ParamField>
                <FieldLabel>Heures min avant</FieldLabel>
                <FieldInput type="number" value={form.min_following_hours} onChange={e => updateField('min_following_hours', e.target.value)} placeholder="48" />
              </ParamField>
              <ParamField>
                <FieldLabel>Délai entre unfollows</FieldLabel>
                <FieldInput value={form.unfollow_delay} onChange={e => updateField('unfollow_delay', e.target.value)} placeholder="ex: 2-5" />
              </ParamField>
              <ParamFieldWide>
                <FieldLabel>Unfollow après X jours</FieldLabel>
                <FieldInput type="number" value={form.unfollow_older_than_days} onChange={e => updateField('unfollow_older_than_days', e.target.value)} placeholder="7" />
              </ParamFieldWide>
            </ParamGrid>
          </Section>

          <Section>
            <SectionTitle><Send size={16} /> Paramètres DM</SectionTitle>
            <ParamGrid>
              <ParamField>
                <FieldLabel>DM aux nouveaux followers</FieldLabel>
                <FieldSelect value={form.dm_new_followers} onChange={e => updateField('dm_new_followers', e.target.value)}>
                  <option value="No">Non</option>
                  <option value="Yes">Oui</option>
                </FieldSelect>
              </ParamField>
              <ParamField>
                <FieldLabel>DM aux follows du bot</FieldLabel>
                <FieldSelect value={form.dm_to_bot_followed} onChange={e => updateField('dm_to_bot_followed', e.target.value)}>
                  <option value="No">Non</option>
                  <option value="Yes">Oui</option>
                </FieldSelect>
              </ParamField>
              <ParamFieldWide>
                <FieldLabel>Limite DM / jour</FieldLabel>
                <FieldInput type="number" value={form.dm_daily_limit} onChange={e => updateField('dm_daily_limit', e.target.value)} placeholder="0" />
              </ParamFieldWide>
              <ParamFieldWide>
                <FieldLabel>Liste de DMs</FieldLabel>
                <FieldTextarea value={form.dm_list} onChange={e => updateField('dm_list', e.target.value)}
                  placeholder="Messages DM séparés par des retours à la ligne..." style={{ minHeight: 60 }} />
              </ParamFieldWide>
            </ParamGrid>
          </Section>

          <Section>
            <SectionTitle><Globe size={16} /> Scraping</SectionTitle>
            <ParamGrid>
              <ParamField>
                <FieldLabel>Limite totale</FieldLabel>
                <FieldInput value={form.total_scrape_limit} onChange={e => updateField('total_scrape_limit', e.target.value)} placeholder="ex: 500" />
              </ParamField>
              <ParamField>
                <FieldLabel>Limite par source</FieldLabel>
                <FieldInput value={form.scrape_limit_per_source} onChange={e => updateField('scrape_limit_per_source', e.target.value)} placeholder="ex: 100" />
              </ParamField>
            </ParamGrid>
          </Section>
        </Column>
      </TwoColumnLayout>

      {/* ── Account Lists (whitelist / blacklist / unfollow) ── */}
      {username && (
        <Section style={{ marginTop: 16, marginBottom: 20 }}>
          <SectionTitle style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FileText size={16} /> Listes du compte</span>
            <span style={{ display: 'flex', gap: 4 }}>
              <button onClick={() => createList('whitelist')} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer' }}>+ Whitelist</button>
              <button onClick={() => createList('blacklist')} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer' }}>+ Blacklist</button>
              <button onClick={() => createList('unfollow')} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer' }}>+ Unfollow</button>
              <button onClick={() => { setShowTargetPicker(true); setTargetSearch(''); setTargetResults([]); setSelectedTargets(new Set()); fetchInteractList(); }}
                style={{ fontSize: 10, padding: '3px 8px', borderRadius: 6, border: '1px solid var(--success)', background: 'rgba(34,197,94,.1)', color: 'var(--success)', cursor: 'pointer', fontWeight: 600 }}>+ Target</button>
            </span>
          </SectionTitle>

          {lists.length === 0 && (
            <div style={{ padding: 16, textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
              Aucune liste trouvée pour @{username}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {lists.map(list => (
              <div key={list.filename} style={{
                padding: '8px 12px', borderRadius: 8, background: 'var(--surface)',
                border: editingList === list.filename ? '1px solid var(--primary)' : '1px solid var(--border)',
                cursor: 'pointer', transition: 'all .15s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
                  onClick={() => editingList === list.filename ? setEditingList(null) : openListEditor(list)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileText size={13} style={{ color: list.type === 'whitelist' ? 'var(--success)' : list.type === 'blacklist' ? 'var(--error)' : 'var(--primary)', flexShrink: 0 }} />
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)' }}>{list.filename}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)', background: 'var(--bg)', padding: '1px 6px', borderRadius: 4 }}>{list.type}</span>
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{list.count} entrées</span>
                  </div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={(e) => { e.stopPropagation(); openProfilePicker(list); }}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', padding: 2, display: 'flex', alignItems: 'center', gap: 2 }}
                      title="Ajouter depuis les interactions"><Users size={13} /><span style={{ fontSize: 10 }}>+</span></button>
                    <button onClick={(e) => { e.stopPropagation(); deleteList(list.filename); }}
                      style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}
                      title="Supprimer"><Trash2 size={13} /></button>
                  </div>
                </div>

                {editingList === list.filename && (
                  <div style={{ marginTop: 8 }}>
                    <FieldTextarea
                      value={listContent}
                      onChange={e => setListContent(e.target.value)}
                      placeholder="Un username par ligne..."
                      style={{ minHeight: 150, fontFamily: 'monospace', fontSize: 11 }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                      <button onClick={() => setEditingList(null)}
                        style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', cursor: 'pointer' }}>
                        Annuler
                      </button>
                      <button onClick={saveList} disabled={listSaving}
                        style={{ fontSize: 11, padding: '4px 12px', borderRadius: 6, border: 'none', background: 'var(--primary)', color: '#fff', cursor: 'pointer', opacity: listSaving ? .6 : 1 }}>
                        <Save size={11} style={{ marginRight: 4 }} />{listSaving ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}
      {/* ── Profile Picker Modal ── */}
      {showProfilePicker && (() => {
        const filtered = interactedProfiles.filter(p =>
          p.username.toLowerCase().includes(pickerSearch.toLowerCase())
        );
        const allVisibleSelected = filtered.length > 0 && filtered.every(p => selectedProfiles.has(p.username));

        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setShowProfilePicker(false)}>
            <div onClick={e => e.stopPropagation()} style={{
              width: '90%', maxWidth: 820, maxHeight: '85vh', background: 'var(--card-bg)',
              borderRadius: 16, border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column',
              boxShadow: '0 20px 60px rgba(0,0,0,.4)'
            }}>
              {/* Header */}
              <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Users size={20} style={{ color: 'var(--primary)' }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Ajouter des profils à {pickerListFile?.filename}
                  </span>
                  {selectedProfiles.size > 0 && (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--primary)', padding: '2px 8px', borderRadius: 10 }}>
                      {selectedProfiles.size} sélectionné{selectedProfiles.size > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <button onClick={() => setShowProfilePicker(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                  <X size={20} />
                </button>
              </div>

              {/* Filters */}
              <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
                  <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                  <input
                    value={pickerSearch}
                    onChange={e => setPickerSearch(e.target.value)}
                    placeholder="Rechercher un profil..."
                    style={{ width: '100%', padding: '7px 10px 7px 30px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 12 }}
                  />
                </div>
                {[
                  { key: 'all', label: 'Tous', icon: <Filter size={12} /> },
                  { key: 'follow', label: 'Follow', icon: <UserPlus size={12} /> },
                  { key: 'like', label: 'Like', icon: <Heart size={12} /> },
                  { key: 'story', label: 'Story', icon: <Eye size={12} /> },
                  { key: 'comment', label: 'Comment', icon: <MessageCircle size={12} /> },
                  { key: 'unfollow', label: 'Unfollow', icon: <UserMinus size={12} /> },
                ].map(f => (
                  <button key={f.key}
                    onClick={() => { setPickerFilter(f.key); fetchInteractedProfiles(f.key); }}
                    style={{
                      padding: '5px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4,
                      border: pickerFilter === f.key ? '1px solid var(--primary)' : '1px solid var(--border)',
                      background: pickerFilter === f.key ? 'rgba(165,145,239,.15)' : 'var(--surface)',
                      color: pickerFilter === f.key ? 'var(--primary)' : 'var(--text-secondary)',
                    }}>
                    {f.icon} {f.label}
                  </button>
                ))}
              </div>

              {/* Select all bar */}
              <div style={{ padding: '8px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button onClick={() => toggleAllVisible(filtered)}
                  style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: 'var(--primary)' }}>
                  {allVisibleSelected ? <CheckSquare size={14} /> : <Square size={14} />}
                  {allVisibleSelected ? 'Tout désélectionner' : `Tout sélectionner (${filtered.length})`}
                </button>
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {profilesLoading ? 'Chargement...' : `${filtered.length} profil${filtered.length !== 1 ? 's' : ''}`}
                </span>
              </div>

              {/* Profile list */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '8px 24px' }}>
                {profilesLoading ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
                    <p style={{ fontSize: 12, marginTop: 8 }}>Chargement des profils...</p>
                  </div>
                ) : filtered.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                    <Users size={32} style={{ opacity: .2 }} />
                    <p style={{ fontSize: 12, marginTop: 8 }}>Aucun profil trouvé</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {filtered.map(p => {
                      const isSelected = selectedProfiles.has(p.username);
                      const lastDate = p.last_interaction ? new Date(p.last_interaction).toLocaleDateString('fr-FR') : '—';
                      return (
                        <div key={p.username}
                          onClick={() => toggleProfile(p.username)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                            borderRadius: 8, cursor: 'pointer', transition: 'all .15s',
                            background: isSelected ? 'rgba(165,145,239,.1)' : 'transparent',
                            border: isSelected ? '1px solid var(--primary)' : '1px solid transparent',
                          }}>
                          {/* Checkbox */}
                          <div style={{ color: isSelected ? 'var(--primary)' : 'var(--text-muted)', flexShrink: 0 }}>
                            {isSelected ? <CheckSquare size={16} /> : <Square size={16} />}
                          </div>
                          {/* Avatar */}
                          <div style={{
                            width: 32, height: 32, borderRadius: '50%', background: 'var(--gradient)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0, overflow: 'hidden'
                          }}>
                            <img src={getLocalProfilePicUrl(p.username)} alt=""
                              onError={e => { e.target.style.display = 'none'; e.target.parentElement.textContent = p.username[0]?.toUpperCase(); }}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          {/* Username + date */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              @{p.username}
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                              Dernière interaction: {lastDate}
                              {p.currently_following && <span style={{ marginLeft: 6, color: 'var(--success)', fontWeight: 600 }}>● Following</span>}
                            </div>
                          </div>
                          {/* Action badges */}
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            {p.follow_count > 0 && (
                              <span title={`${p.follow_count} follow(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(34,197,94,.12)', color: '#22c55e' }}>
                                <UserPlus size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{p.follow_count}
                              </span>
                            )}
                            {p.like_count > 0 && (
                              <span title={`${p.like_count} like(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(239,68,68,.12)', color: '#ef4444' }}>
                                <Heart size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{p.like_count}
                              </span>
                            )}
                            {p.story_count > 0 && (
                              <span title={`${p.story_count} story action(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(245,158,11,.12)', color: '#f59e0b' }}>
                                <Eye size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{p.story_count}
                              </span>
                            )}
                            {p.comment_count > 0 && (
                              <span title={`${p.comment_count} comment(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(59,130,246,.12)', color: '#3b82f6' }}>
                                <MessageCircle size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{p.comment_count}
                              </span>
                            )}
                            {p.unfollow_count > 0 && (
                              <span title={`${p.unfollow_count} unfollow(s)`} style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'rgba(107,114,128,.12)', color: '#6b7280' }}>
                                <UserMinus size={9} style={{ marginRight: 2, verticalAlign: 'middle' }} />{p.unfollow_count}
                              </span>
                            )}
                            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px', borderRadius: 4, background: 'var(--surface)', color: 'var(--text-muted)' }}>
                              {p.total_actions}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <button onClick={() => setShowProfilePicker(false)}
                  style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Annuler
                </button>
                <button onClick={addSelectedToList} disabled={selectedProfiles.size === 0 || addingProfiles}
                  style={{
                    padding: '8px 20px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                    background: selectedProfiles.size > 0 ? 'var(--gradient)' : 'var(--surface)',
                    color: selectedProfiles.size > 0 ? '#fff' : 'var(--text-muted)',
                    opacity: addingProfiles ? .6 : 1, display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                  <Plus size={14} />
                  {addingProfiles ? 'Ajout...' : `Ajouter ${selectedProfiles.size} profil${selectedProfiles.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ── Target Picker Modal ── */}
      {showTargetPicker && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowTargetPicker(false)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: '90%', maxWidth: 900, maxHeight: '85vh', background: 'var(--card-bg)',
            borderRadius: 16, border: '1px solid var(--card-border)', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,.4)'
          }}>
            {/* Header */}
            <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Target size={20} style={{ color: 'var(--success)' }} />
                <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                  Liste d'interaction — @{username}
                </span>
                {interactList.filter(e => e.status === 'pending').length > 0 && (
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: 'var(--success)', padding: '2px 8px', borderRadius: 10 }}>
                    {interactList.filter(e => e.status === 'pending').length} en attente
                  </span>
                )}
              </div>
              <button onClick={() => setShowTargetPicker(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Left: Search & add profiles */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: '1px solid var(--border)' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>Rechercher des profils</div>
                  <div style={{ position: 'relative' }}>
                    <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                    <input
                      value={targetSearch}
                      onChange={e => { setTargetSearch(e.target.value); searchTargetProfiles(e.target.value); }}
                      placeholder="Rechercher un profil (min 2 caractères)..."
                      style={{ width: '100%', padding: '8px 10px 8px 30px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text-primary)', fontSize: 12, boxSizing: 'border-box' }}
                    />
                  </div>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                  {targetSearching ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)' }}>
                      <RefreshCw size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : targetResults.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: 'var(--text-muted)', fontSize: 12 }}>
                      {targetSearch.length >= 2 ? 'Aucun profil trouvé' : 'Tapez pour rechercher...'}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {targetResults.map(p => {
                        const isSel = selectedTargets.has(p.username);
                        return (
                          <div key={p.username} onClick={() => toggleTarget(p.username)} style={{
                            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                            background: isSel ? 'rgba(34,197,94,.1)' : 'transparent',
                            border: isSel ? '1px solid var(--success)' : '1px solid transparent',
                          }}>
                            <div style={{ color: isSel ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0 }}>
                              {isSel ? <CheckSquare size={14} /> : <Square size={14} />}
                            </div>
                            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700, flexShrink: 0, overflow: 'hidden' }}>
                              <img src={getLocalProfilePicUrl(p.username)} alt="" onError={e => { e.target.style.display='none'; e.target.parentElement.textContent=p.username[0]?.toUpperCase(); }} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{p.username}</div>
                              <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                                {p.followers_count != null ? `${p.followers_count} followers` : ''}{p.full_name ? ` · ${p.full_name}` : ''}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {selectedTargets.size > 0 && (
                  <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)' }}>
                    <button onClick={addTargetsToInteractList} disabled={addingTargets}
                      style={{ width: '100%', padding: '9px 16px', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'var(--success)', color: '#fff', opacity: addingTargets ? .6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      <Plus size={14} />
                      {addingTargets ? 'Ajout...' : `Ajouter ${selectedTargets.size} profil(s)`}
                    </button>
                  </div>
                )}
              </div>

              {/* Right: Current interact list */}
              <div style={{ width: 320, display: 'flex', flexDirection: 'column' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                    Liste actuelle ({interactList.length})
                  </div>
                  <button onClick={fetchInteractList} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2 }}>
                    <RefreshCw size={13} />
                  </button>
                </div>
                <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                  {interactListLoading ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                      <RefreshCw size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : interactList.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)', fontSize: 11 }}>
                      Aucune cible dans la liste
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                      {interactList.map(e => (
                        <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '5px 8px', borderRadius: 6, background: e.status === 'pending' ? 'rgba(34,197,94,.05)' : 'var(--surface)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: e.status === 'pending' ? 'var(--success)' : 'var(--text-muted)', flexShrink: 0 }} />
                            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>@{e.target_username}</span>
                          </div>
                          <button onClick={() => removeFromInteractList([e.target_username])}
                            style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 2, flexShrink: 0 }}>
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Page>
  );
};

export default AccountProfile;
